import React, { useState, useEffect } from "react";
import ScalesPreview from "../shared/ScalesPreview/ScalesPreview";
import { getApiEndpoint } from "../../config/config";
import { useAuth } from "../../context/AuthContext";
import StandardModal from "../shared/Modal/StandardModal";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";
import {
  TextInput,
  Button,
  Group,
  Stack,
  Table,
  NumberInput,
  ActionIcon,
  Text,
  Paper,
  Grid,
} from "@mantine/core";
import { IconPlus, IconX } from "@tabler/icons-react";

function DefaultScaleModal({ isOpen, onClose, type, selectedScale = null }) {
  const [scaleTemplates, setScaleTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [currentScale, setCurrentScale] = useState([]);
  const [scaleName, setScaleName] = useState("");
  const { getVerifiedJWT } = useAuth();

  const getScaleEndpoint = (scaleType, templateId = null) => {
    const basePath = `/v1/scales/default-scales`;

    if (templateId) {
      return `${basePath}/${templateId}`;
    }

    if (scaleType === "MD") return `${basePath}/MD`;
    if (scaleType === "OVERRIDE") return `${basePath}/OVERRIDE`;
    if (scaleType === "COMMISSION") return `${basePath}/COMMISSION`;

    return "";
  };

  // Load scale templates from API on component mount or when selectedScale changes
  useEffect(() => {
    if (isOpen) {
      loadScaleTemplates();

      // If a selected scale is provided, automatically open it in edit mode
      if (selectedScale) {
        startEditing(selectedScale);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedScale]);

  // API functions to fetch and save templates
  const loadScaleTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();

      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const url = getScaleEndpoint(type);

      if (!url) {
        return;
      }

      const response = await fetch(getApiEndpoint(url), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setScaleTemplates(data.data);
    } catch (err) {
      setError("Failed to load scale templates");
      setScaleTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (template, create_scale = true) => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const url_string = getScaleEndpoint(type);

      if (!url_string) {
        return;
      }

      const url = getApiEndpoint(url_string);
      const requestBody = template;

      const response = await fetch(url, {
        method: create_scale ? "POST" : "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setScaleTemplates(data.data);
    } catch (err) {
      setError("Failed to save scale templates");
    } finally {
      setLoading(false);
    }
  };

  const startCreating = () => {
    setIsCreating(true);
    setEditingTemplate(null);
    setScaleName("");
    setCurrentScale([{ id: 1, revenue_amount: "", percentage: "" }]);
  };

  const startEditing = template => {
    setEditingTemplate(template);
    setScaleName(template.name);
    // Map scale lines with proper IDs for editing
    const scalesWithIds = template.scale_lines.map((line, index) => ({
      id: line.id || index + 1,
      revenue_amount: line.revenue_amount,
      percentage: line.percentage,
    }));
    setCurrentScale(scalesWithIds);
    setIsCreating(false);
  };

  const cancelEditing = () => {
    setIsCreating(false);
    setEditingTemplate(null);
    setScaleName("");
    setCurrentScale([]);
  };

  const handleScaleChange = (id, field, value) => {
    setCurrentScale(prev =>
      prev.map(item => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  const addRow = () => {
    const newId = Math.max(...currentScale.map(item => item.id)) + 1;
    setCurrentScale(prev => [
      ...prev,
      {
        id: newId,
        revenue_amount: "",
        percentage: "",
      },
    ]);
  };

  const removeRow = id => {
    if (currentScale.length > 1) {
      setCurrentScale(prev => prev.filter(item => item.id !== id));
    }
  };

  const validateTemplate = async () => {
    if (!scaleName.trim()) {
      setError("Please enter a name for the template");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Filter out empty rows
      const validScales = currentScale.filter(
        scale => scale.revenue_amount || scale.percentage
      );

      if (validScales.length === 0) {
        setError("Please add at least one entry");
        return;
      }

      // Process scale lines consistently for both create and update
      const processedScaleLines = validScales.map(scale => ({
        revenue_amount: parseFloat(scale.revenue_amount) || 0,
        percentage: parseFloat(scale.percentage) || 0,
      }));

      if (editingTemplate) {
        // Update existing template
        const editedTemplate = {
          id: editingTemplate.id,
          name: scaleName,
          scale_lines: processedScaleLines,
        };
        await saveTemplate(editedTemplate, false);
      } else {
        // Create new template
        const newTemplate = {
          name: scaleName,
          scale_lines: processedScaleLines,
        };
        await saveTemplate(newTemplate, true);
      }

      cancelEditing();
    } catch (err) {
      setError("Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async templateId => {
    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint(getScaleEndpoint(type, templateId)),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Reload the templates after successful deletion
      await loadScaleTemplates();
    } catch (err) {
      setError("Failed to delete template");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    cancelEditing();
    onClose();
  };

  if (!isOpen) return null;

  // Render template cards view
  const renderTemplatesView = () => (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        {`Create and manage ${
          type === "MD"
            ? "Marketing Deal"
            : type === "OVERRIDE"
              ? "override"
              : "commission scale"
        } templates that can be applied when adding new
        reps. Each template can have a custom name and different commission
        structures.`}
      </Text>

      <Group justify="space-between" align="center">
        <Text size="lg" fw={600}>
          {type === "MD"
            ? "Marketing Deal"
            : type === "OVERRIDE"
              ? "Override"
              : "Commission"}{" "}
          Templates
        </Text>

        {scaleTemplates.length > 0 && (
          <Button
            variant="outline"
            leftSection={<IconPlus size={16} />}
            onClick={startCreating}
          >
            Create New Template
          </Button>
        )}
      </Group>

      <Grid>
        {scaleTemplates.map(template => (
          <Grid.Col key={template.id} span={{ base: 12, sm: 12, lg: 12 }}>
            <ScalesPreview
              scale_lines={template.scale_lines}
              name={template.name}
              createdAt={template.createdAt}
              onEdit={updatedData => {
                const processedScaleLines = updatedData.scale_lines.map(
                  scale => ({
                    revenue_amount: parseFloat(scale.revenue_amount) || 0,
                    percentage: parseFloat(scale.percentage) || 0,
                  })
                );

                const updatedTemplate = {
                  id: template.id,
                  name: updatedData.name,
                  scale_lines: processedScaleLines,
                };

                saveTemplate(updatedTemplate, false);
              }}
              onDelete={() => deleteTemplate(template.id)}
              showActions={true}
              showFooter={true}
            />
          </Grid.Col>
        ))}
      </Grid>

      {scaleTemplates.length === 0 && (
        <Paper p="xl" ta="center" withBorder>
          <Text mb="md">No templates created yet.</Text>
          <Button
            variant="outline"
            leftSection={<IconPlus size={16} />}
            onClick={startCreating}
          >
            Create Your First Template
          </Button>
        </Paper>
      )}
    </Stack>
  );

  // Render template form view
  const renderTemplateForm = () => (
    <Stack gap="lg">
      <Text size="lg" fw={600}>
        {editingTemplate
          ? `Edit "${editingTemplate.name}"`
          : "Create New Template"}
      </Text>

      <TextInput
        label="Template Name"
        value={scaleName}
        onChange={e => setScaleName(e.target.value)}
        placeholder="e.g., Standard, Premium Rate, etc."
        required
        disabled={loading}
      />

      <Stack gap="xs">
        <Text fw={500}>Scale Configuration</Text>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th width={140}>Percentage (%)</Table.Th>
              <Table.Th width={140}>Revenue ($)</Table.Th>
              <Table.Th width={80}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {currentScale.map(item => (
              <Table.Tr key={item.id}>
                <Table.Td>
                  <NumberInput
                    value={item.percentage}
                    onChange={value =>
                      handleScaleChange(item.id, "percentage", value || "")
                    }
                    placeholder="0.00"
                    min={0}
                    max={100}
                    decimalScale={2}
                    disabled={loading}
                    style={{ width: "120px" }}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={item.revenue_amount}
                    onChange={value =>
                      handleScaleChange(item.id, "revenue_amount", value || "")
                    }
                    placeholder="0.00"
                    min={0}
                    decimalScale={2}
                    disabled={loading}
                    style={{ width: "120px" }}
                  />
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    color="red"
                    variant="subtle"
                    onClick={() => removeRow(item.id)}
                    disabled={currentScale.length <= 1 || loading}
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        <Group justify="flex-start">
          <Button
            variant="outline"
            size="sm"
            leftSection={<IconPlus size={16} />}
            onClick={addRow}
            disabled={loading}
          >
            Add Row
          </Button>
        </Group>
      </Stack>
    </Stack>
  );

  const footerRight =
    isCreating || editingTemplate ? (
      <Group>
        <Button variant="outline" onClick={cancelEditing} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={validateTemplate} disabled={loading} loading={loading}>
          {editingTemplate ? "Update Template" : "Create Template"}
        </Button>
      </Group>
    ) : (
      <Button variant="outline" onClick={handleClose} disabled={loading}>
        Close
      </Button>
    );

  if (!isOpen) return null;

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        type === "MD"
          ? "Manage Default Marketing Deal Scales"
          : type === "OVERRIDE"
            ? "Manage Default Override Scales"
            : "Manage Default Commission Scales"
      }
      footerLeft={<div></div>}
      footerRight={footerRight}
      size={isCreating || editingTemplate ? "500px" : "lg"}
    >
      <Stack gap="md">
        {error && <DisplayMessage type="error" message={error} />}
        {isCreating || editingTemplate
          ? renderTemplateForm()
          : renderTemplatesView()}
      </Stack>
    </StandardModal>
  );
}

export default DefaultScaleModal;
